/**
 * Copy field, the measured field. Screen spec 4.7, Instrument motif 4.
 *
 * Every copyable literal in the product sits in this one atom: a sunken slab,
 * a hairline, mono type, and a copy control pinned right that never moves and
 * never shifts layout when its glyph swaps to a check for 1500ms. One
 * component renders the web address, the connection details, a key name, a
 * commit hash, a SQL statement and the claim link. It is the most repeated
 * visual in the product, which is exactly why it gets no decorative flourish.
 *
 * The control is a fixed square holding both glyphs stacked in a grid cell, so
 * the check is the same width as the copy glyph by construction rather than by
 * measurement. Nothing reflows, at any string length, in any language.
 *
 * No toast. The check is the receipt, and a toast on top of it is noise.
 *
 * URLs truncate from the middle, so the subdomain and the ending both stay
 * readable. Truncating the tail of a web address hides the part that tells you
 * which app you are looking at.
 */

import { useEffect, useRef, useState } from 'react';
import { useClipboard } from '../../lib/hooks/use-clipboard';
import { middleTruncate } from '../../lib/format';
import { t } from '../../lib/i18n/t';
import { captureEvent } from '../../lib/analytics/posthog';
import '../../styles/components/copy-field.css';

/** The sensitive variant re-masks itself, so a revealed key does not sit on a
 *  screen behind someone's back for the rest of the afternoon. */
export const REMASK_MS = 30_000;

export type CopyFieldSize = 'inline' | 'md' | 'lg';

export interface CopyFieldProps {
  /** Names the value for the announcement: "Web address copied." */
  label: string;
  value: string;
  size?: CopyFieldSize;
  /**
   * Prints the label above the slab. Off by default, because almost every
   * copy field in the product sits under a heading that already names it and a
   * second name would be noise. On where several slabs stack with nothing to
   * tell them apart: two DNS records are four identical slabs, and which one is
   * the name and which is the value is not guessable from the values.
   */
  showLabel?: boolean;
  /** Middle truncation budget in characters. Omit to show the whole value. */
  truncateAt?: number;
  /** Masks the value behind a Reveal control that re-masks after 30 seconds. */
  sensitive?: boolean;
  /**
   * Fires once the value actually reached the clipboard. Optional, and only
   * for a screen whose whole purpose is the copy: reveal and re-mask are
   * captured here already, but the copy itself was invisible, so a screen
   * that exists to hand somebody one string had no way to know it worked.
   * Never given the value, which may be a key.
   */
  onCopied?: () => void;
}

const CHECK = 'M3 8.5 6.5 12 13 4.5';

export function CopyField({
  label,
  value,
  size = 'md',
  truncateAt,
  sensitive = false,
  showLabel = false,
  onCopied,
}: CopyFieldProps) {
  const { copy, copied, failed } = useClipboard();
  const [revealed, setRevealed] = useState(false);
  const valueRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!revealed) return;
    const id = window.setTimeout(() => setRevealed(false), REMASK_MS);
    return () => window.clearTimeout(id);
  }, [revealed]);

  const masked = sensitive && !revealed;
  const shown = masked
    ? '•'.repeat(Math.min(24, Math.max(8, value.length)))
    : truncateAt
      ? middleTruncate(value, truncateAt)
      : value;

  async function onCopy() {
    const ok = await copy(value, label);
    if (ok) {
      onCopied?.();
      return;
    }
    // Clipboard refused, which happens over plain http and inside some
    // embedded views. Pre-select the literal so the fallback is a keystroke
    // rather than a dead end.
    const node = valueRef.current;
    if (!node) return;
    const range = document.createRange();
    range.selectNodeContents(node);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }

  return (
    <div className="copyf" data-size={size} data-copied={copied ? 'yes' : 'no'}>
      {showLabel ? <span className="copyf__label">{label}</span> : null}

      <div
        className="copyf__slab"
        onClick={masked ? undefined : () => void onCopy()}
        role="presentation"
      >
        <span className="copyf__value mono" ref={valueRef} title={masked ? undefined : value}>
          {shown}
        </span>

        {sensitive ? (
          <button
            type="button"
            className="copyf__reveal"
            onClick={(event) => {
              event.stopPropagation();
              setRevealed((on) => {
                const next = !on;
                captureEvent(next ? 'secret_revealed' : 'secret_remasked', {
                  label,
                  screen_path: window.location.pathname,
                });
                return next;
              });
            }}
          >
            {revealed ? t('Hide') : t('Reveal')}
          </button>
        ) : null}

        <button
          type="button"
          className="copyf__btn"
          onClick={(event) => {
            event.stopPropagation();
            void onCopy();
          }}
          aria-label={t('Copy {label}', { label })}
          disabled={masked}
        >
          <span className="copyf__glyphs" aria-hidden="true">
            <svg className="copyf__glyph" data-on={copied ? 'no' : 'yes'} viewBox="0 0 16 16" width="14" height="14" focusable="false">
              <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
              <path d="M10.5 3.5H3.5a1 1 0 0 0-1 1v7" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
            <svg className="copyf__glyph" data-on={copied ? 'yes' : 'no'} viewBox="0 0 16 16" width="14" height="14" focusable="false">
              <path d={CHECK} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </button>
      </div>

      <span className="copyf__say" role="status" aria-live="polite">
        {copied ? t('{label} copied.', { label }) : ''}
      </span>

      {failed ? (
        <p className="copyf__fallback">{t('Copy this and paste it to your coding agent.')}</p>
      ) : null}
    </div>
  );
}
